/**
 * Where the AI badge sits over the asset it labels.
 *
 * The corner is a placement decision, not a styling one: the badge is a compliance
 * artifact, so an editor must be able to move it off the one part of a picture where it
 * covers a face, a logo or burnt-in text — without a deploy, and without the option to
 * hide it altogether. That is why this is a closed set of four corners rather than a
 * free-form offset.
 */
export type AiBadgePosition =
  | "top-left"
  | "top-right"
  | "bottom-left"
  | "bottom-right";

export const AI_BADGE_POSITIONS: readonly AiBadgePosition[] = [
  "top-left",
  "top-right",
  "bottom-left",
  "bottom-right",
];

/**
 * The corner every badge used before the setting existed.
 *
 * Load-bearing as a default rather than merely conventional: an unset global, an
 * unreadable config story and a registry entry written by an older run all resolve here,
 * and all three must land on the placement the sweep was reviewed against.
 */
export const DEFAULT_AI_BADGE_POSITION: AiBadgePosition = "bottom-right";

/** The `aiImagesConfig` field holding the site-wide default. */
export const AI_BADGE_POSITION_FIELD = "badge_position";

export function isAiBadgePosition(value: unknown): value is AiBadgePosition {
  return (
    typeof value === "string" &&
    (AI_BADGE_POSITIONS as readonly string[]).includes(value)
  );
}

/** For the admin page's picker, so the four labels have one source of truth. */
export const AI_BADGE_POSITION_LABEL: Record<AiBadgePosition, string> = {
  "top-left": "Top left",
  "top-right": "Top right",
  "bottom-left": "Bottom left",
  "bottom-right": "Bottom right",
};

/**
 * The site-wide default from an `aiImagesConfig` story's content.
 *
 * Anything unrecognised — absent, blank, or a value hand-typed into the field — falls
 * back to the default rather than throwing or rendering nothing. A badge that fails to
 * appear is the one outcome this feature must never produce.
 */
export function readBadgePosition(
  content: Record<string, unknown> | null | undefined,
): AiBadgePosition {
  const value = content?.[AI_BADGE_POSITION_FIELD];

  return isAiBadgePosition(value) ? value : DEFAULT_AI_BADGE_POSITION;
}

/**
 * The corner as two booleans, for geometry that has to reason about edges.
 *
 * Both the crop that samples the background and the CSS that pins the badge need the
 * same decomposition, and deriving it here is what keeps a measured corner and a
 * rendered corner from drifting apart — a mismatch there means the contrast is judged
 * from pixels the badge does not cover, which is exactly how a badge goes invisible.
 */
export function badgeAnchor(position: AiBadgePosition): {
  top: boolean;
  left: boolean;
} {
  return {
    top: position === "top-left" || position === "top-right",
    left: position === "top-left" || position === "bottom-left",
  };
}
